import type { ReactNode } from 'react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { cn } from '@sara/ui/lib/utils';

interface QueryErrorBoundaryProps {
  children: ReactNode;
  fallbackRender?: (props: { error: unknown; resetErrorBoundary: () => void }) => ReactNode;
}

function DefaultFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
      <p>Something went wrong.</p>
      <button
        type="button"
        onClick={resetErrorBoundary}
        className={cn(
          'mt-2 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground',
          'hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        Try again
      </button>
    </div>
  );
}

export function QueryErrorBoundary({ children, fallbackRender }: QueryErrorBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={
            fallbackRender
              ? ({ error, resetErrorBoundary }: FallbackProps) => (
                  <>{fallbackRender({ error, resetErrorBoundary })}</>
                )
              : DefaultFallback
          }
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
